import { FC, useContext, useState, ReactNode } from 'react';
import clsx from 'clsx';

import {
  AuthContext,
  SignUpForm,
  SignInForm,
  Subtitle,
  urls,
  LazyIcon,
} from '@getstream/ui-library/build';

import Footer from '../Footer';
import Title from '../Title';
import Button from '../Button';
import Link from '../Link';

import { Product } from '../../../types/tour';

import styles from './Introduction.module.css';

export type Props = {
  image: ReactNode;
  subtitle: string | ReactNode;
  title: string;
  product: Product.Chat | Product.ActivityFeeds | Product.Video;
  setComplete: () => void;
  noAuth?: boolean;
  icon?: string;
};

export const LinkComponent = ({
  strong,
  ...props
}: {
  href?: string;
  to?: string;
  strong: boolean;
}) => {
  return <a href={props.href || props.to} {...props} />;
};

export const Introduction: FC<Props> = ({
  setComplete,
  product,
  title,
  subtitle,
  image,
  noAuth,
  icon,
}) => {
  const { isLoggedIn, email, username, setLogin }: any =
    useContext(AuthContext);
  const [loginForm, setLoginForm] = useState(false);
  const currentPage = window.location.href;

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <Title className={styles.title}>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>

          <div className={styles.signIn}>
            <div className={styles.form}>
              {isLoggedIn && !noAuth && (
                <div>
                  <p className={styles.description}>
                    You are logged in as <b className="var-bold">{username}</b>{' '}
                    ({email}).
                  </p>
                  <Button size="medium" color="primary" onClick={setComplete}>
                    Continue
                  </Button>
                </div>
              )}
              {noAuth && (
                <Button size="medium" color="primary" onClick={setComplete}>
                  Continue
                </Button>
              )}
              {!isLoggedIn && !loginForm && !noAuth && (
                <SignUpForm
                  apiBaseURL={import.meta.env.VITE_API_BASEURL}
                  googleClientID={import.meta.env.VITE_GOOGLE_CLIENT_ID}
                  githubClientID={import.meta.env.VITE_GITHUB_CLIENT_ID}
                  label="Get Started"
                  githubLabel="Continue with GitHub"
                  next={`${urls.onboarding}?next=${currentPage}`}
                  redirect
                  setLogin={setLogin}
                  navigateCallback={(url: string) =>
                    (window.location.href = url)
                  }
                  LinkComponent={LinkComponent}
                />
              )}
              {!isLoggedIn && loginForm && !noAuth && (
                <SignInForm
                  apiBaseURL={import.meta.env.VITE_API_BASEURL}
                  googleClientID={import.meta.env.VITE_GOOGLE_CLIENT_ID}
                  githubClientID={import.meta.env.VITE_GITHUB_CLIENT_ID}
                  redirectCallback={(url: string, state: any) => {
                    history.pushState(state, '', url);
                  }}
                  label="Log In"
                  LinkComponent={LinkComponent}
                />
              )}
            </div>
          </div>

          <div className={'text-grey text-xs'}>
            {!isLoggedIn && !loginForm && !noAuth && (
              <>
                <p className="mb-4">
                  By joining, you agree to our{' '}
                  <Link className={styles.link} href={urls.terms}>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link className={styles.link} href={urls.privacy}>
                    Privacy Policy
                  </Link>
                  .
                </p>
              </>
            )}
            {!isLoggedIn && loginForm && !noAuth && (
              <p className="mb-4">
                Don't have an account?{' '}
                <Link blue href="#" onClick={() => setLoginForm(false)}>
                  Click here
                </Link>{' '}
                to sign up.
              </p>
            )}
          </div>
        </div>
        <div className={'w-1/2'}>{image}</div>
      </div>

      <Footer product={product} />
    </div>
  );
};
